// Brand identity schema for profile/config.json's optional `branding` block.
//
// Pure module on purpose: no DOM, no React, no fetch. vite.config.ts imports
// it to refuse a build whose profile config carries a malformed branding
// block, and src/lib/branding.ts imports it to apply the same contract at
// runtime. One validator, two enforcement points.
//
// Contract (docs/how-to/applying-the-brand-pack.md is the operator-facing
// statement of the same rules):
//   - `branding` absent entirely  -> the shipped Devoteam defaults below.
//   - `branding` present          -> every key it carries is validated
//     strictly; an unknown key, a wrong type, an empty string or an
//     unparseable colour fails the build and the app bootstrap loudly.
//     There is no silent fallback for a present-but-broken block: a typo'd
//     brand pack must never ship a half-Devoteam, half-customer UI.
//   - Keys the block does not carry keep their Devoteam default, so a pack
//     only states what it changes.

export class BrandingConfigError extends Error {
  constructor(message: string) {
    super(`Invalid branding config: ${message}`)
    this.name = "BrandingConfigError"
  }
}

/**
 * Colour tokens, each mapping 1:1 onto a CSS custom property declared in
 * src/styles/globals.css. The internal property names keep their historical
 * "coral" spelling so the stylesheet diff stays reviewable; the config keys
 * are brand-neutral.
 */
export interface BrandingColors {
  /** Brand accent. CSS: --brand-coral (shipped hsl(0, 82%, 61%)). */
  primary?: string
  /** Lighter accent for hovers on dark ground. CSS: --brand-coral-soft. */
  primary_soft?: string
  /** Darker accent, used for links. CSS: --brand-coral-deep. */
  primary_deep?: string
  /** Darkest accent, used for link hover. CSS: --brand-coral-strong. */
  primary_strong?: string
  /** Near-white accent tint. CSS: --brand-coral-wash. */
  primary_wash?: string
  /** Near-black text/surfaces. CSS: --brand-ink. */
  ink?: string
  /** Softer body-text ink. CSS: --brand-ink-soft. */
  ink_soft?: string
  /** Light text on dark surfaces. CSS: --brand-mist. */
  mist?: string
}

export interface Branding {
  /** Product name used in running copy ("Ask <product> anything"). */
  productName: string
  /**
   * Full formal product name, in its legal register. Rendered exactly once,
   * on the About page footer; every other surface carries the wordmark or
   * the product name. Kept as its own field because the customer's name
   * contract states the legal name separately from the mark.
   */
  formalProductName: string
  /** Text mark rendered beside the logo monogram (gate card, headers). */
  wordmark: string
  /**
   * Deployment owner, rendered beside the fixed "Delivered by Devoteam"
   * credit on the sign-in screen and in the about-page copy; also the
   * assistant avatar initial.
   */
  vendorName: string
  /**
   * Reserved: nothing renders this since the about page's vendor CTA became
   * the fixed delivery credit. Still validated so a pack stating it is not
   * rejected, and so a future CTA has a home.
   */
  vendorUrl: string
  /** document.title of the standalone app. Derived from vendor + product when the pack does not set it. */
  browserTitle: string
  /** document.title of the embedded support desk panel. */
  panelTitle: string
  /** Public path of the header/about logo asset. */
  logoPath: string
  /** Public path of the favicon; only rewritten when the pack sets it. */
  faviconPath?: string
  /** Colour tokens the pack provided. Empty object = no runtime overrides. */
  colors: BrandingColors
}

export const DEFAULT_BRANDING: Branding = {
  productName: "Insight",
  formalProductName: "Devoteam Insight",
  wordmark: "insight",
  vendorName: "Devoteam",
  vendorUrl:
    "https://www.devoteam.com/uk/amazon-web-services/accelerate-ai-adoption-with-adapt-for-aws/",
  browserTitle: "Devoteam Insight",
  panelTitle: "Insight Panel",
  logoPath: "/logo.svg",
  colors: {},
}

/** Config key -> CSS custom property set on <html> at bootstrap. */
export const COLOR_KEY_TO_CSS_VAR: Record<keyof BrandingColors, string> = {
  primary: "--brand-coral",
  primary_soft: "--brand-coral-soft",
  primary_deep: "--brand-coral-deep",
  primary_strong: "--brand-coral-strong",
  primary_wash: "--brand-coral-wash",
  ink: "--brand-ink",
  ink_soft: "--brand-ink-soft",
  mist: "--brand-mist",
}

const KNOWN_BRANDING_KEYS = [
  "product_name",
  "formal_product_name",
  "wordmark",
  "vendor_name",
  "vendor_url",
  "browser_title",
  "panel_title",
  "logo_path",
  "favicon_path",
  "colors",
] as const

const KNOWN_COLOR_KEYS = Object.keys(COLOR_KEY_TO_CSS_VAR)

// Deliberately narrower than CSS: a flat hex value or a single colour
// function with no nested functions. Rejects var(), url(), color-mix() and
// anything that could smuggle extra declarations into an inline style. The
// config file is first-party, but the same values reach
// document.documentElement.style, so the gate is structural, not trust-based.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const FUNCTION_COLOR = /^(?:hsl|hsla|rgb|rgba|oklch|oklab|lab|lch|hwb)\([^(){};]*\)$/i

export function isValidBrandingColor(value: string): boolean {
  const v = value.trim()
  return HEX_COLOR.test(v) || FUNCTION_COLOR.test(v)
}

function requireNonEmptyString(value: unknown, key: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new BrandingConfigError(`\`${key}\` must be a non-empty string`)
  }
  return value.trim()
}

function requireRecord(value: unknown, key: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new BrandingConfigError(`\`${key}\` must be an object`)
  }
  return value as Record<string, unknown>
}

function rejectUnknownKeys(
  record: Record<string, unknown>,
  known: readonly string[],
  where: string
): void {
  const unknown = Object.keys(record).filter(k => !known.includes(k))
  if (unknown.length > 0) {
    throw new BrandingConfigError(
      `unknown key(s) in ${where}: ${unknown.join(", ")}. Known keys: ${known.join(", ")}`
    )
  }
}

function parseColors(raw: unknown): BrandingColors {
  const record = requireRecord(raw, "branding.colors")
  rejectUnknownKeys(record, KNOWN_COLOR_KEYS, "branding.colors")
  const colors: BrandingColors = {}
  for (const key of Object.keys(record)) {
    const value = requireNonEmptyString(record[key], `branding.colors.${key}`)
    if (!isValidBrandingColor(value)) {
      throw new BrandingConfigError(
        `\`branding.colors.${key}\` is not a plain CSS colour: "${value}". ` +
          `Use a hex value or a single hsl()/rgb()/oklch() function.`
      )
    }
    colors[key as keyof BrandingColors] = value
  }
  return colors
}

/**
 * Validate the `branding` value from profile/config.json and resolve it
 * against the shipped Devoteam defaults.
 *
 * @throws BrandingConfigError when the block is present but malformed.
 */
export function parseBranding(raw: unknown): Branding {
  if (raw === undefined || raw === null) {
    return DEFAULT_BRANDING
  }
  const record = requireRecord(raw, "branding")
  rejectUnknownKeys(record, KNOWN_BRANDING_KEYS, "branding")

  const productName =
    record.product_name !== undefined
      ? requireNonEmptyString(record.product_name, "branding.product_name")
      : DEFAULT_BRANDING.productName
  const vendorName =
    record.vendor_name !== undefined
      ? requireNonEmptyString(record.vendor_name, "branding.vendor_name")
      : DEFAULT_BRANDING.vendorName

  let vendorUrl = DEFAULT_BRANDING.vendorUrl
  if (record.vendor_url !== undefined) {
    vendorUrl = requireNonEmptyString(record.vendor_url, "branding.vendor_url")
    let parsed: URL
    try {
      parsed = new URL(vendorUrl)
    } catch {
      throw new BrandingConfigError(`\`branding.vendor_url\` is not a valid URL: "${vendorUrl}"`)
    }
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      throw new BrandingConfigError(
        `\`branding.vendor_url\` must be http(s), got "${parsed.protocol}"`
      )
    }
  }

  // Titles derive from the resolved names unless the pack states them, so a
  // pack that only sets product_name/vendor_name still gets coherent tabs.
  // With the shipped defaults the derived values are byte-identical to the
  // static <title> tags in index.html and embed.html.
  const browserTitle =
    record.browser_title !== undefined
      ? requireNonEmptyString(record.browser_title, "branding.browser_title")
      : record.product_name !== undefined || record.vendor_name !== undefined
        ? `${vendorName} ${productName}`
        : DEFAULT_BRANDING.browserTitle
  const panelTitle =
    record.panel_title !== undefined
      ? requireNonEmptyString(record.panel_title, "branding.panel_title")
      : record.product_name !== undefined
        ? `${productName} Panel`
        : DEFAULT_BRANDING.panelTitle
  const wordmark =
    record.wordmark !== undefined
      ? requireNonEmptyString(record.wordmark, "branding.wordmark")
      : record.product_name !== undefined
        ? productName.toLowerCase()
        : DEFAULT_BRANDING.wordmark
  // Same derivation shape as browser_title: a pack that states names but not
  // the formal name still gets a coherent legal line on the About page.
  const formalProductName =
    record.formal_product_name !== undefined
      ? requireNonEmptyString(record.formal_product_name, "branding.formal_product_name")
      : record.product_name !== undefined || record.vendor_name !== undefined
        ? `${vendorName} ${productName}`
        : DEFAULT_BRANDING.formalProductName

  const branding: Branding = {
    productName,
    formalProductName,
    wordmark,
    vendorName,
    vendorUrl,
    browserTitle,
    panelTitle,
    logoPath:
      record.logo_path !== undefined
        ? requireNonEmptyString(record.logo_path, "branding.logo_path")
        : DEFAULT_BRANDING.logoPath,
    colors: record.colors !== undefined ? parseColors(record.colors) : {},
  }
  if (record.favicon_path !== undefined) {
    branding.faviconPath = requireNonEmptyString(record.favicon_path, "branding.favicon_path")
  }
  return branding
}
